import { Input } from '@components/ui/Input/Input';
import { Spinner } from '@components/ui/Spinner/Spinner';
import { Button, CircularProgress } from '@mui/material';
import {
  useEscalateServiceCheckMutation,
  useForceServiceCheckMutation,
  useServiceCheckQuery,
  useSubmitServiceCheckMutation,
} from '@dieterApi/snippet';
import { useUser } from '@app/context/UserContext';
import { useAvailableServicesForCategoryQuery } from '@dieterApi/snippet/useAvailableServicesForCategoryQuery';
import { humanizeCategoryKeysInText, snippetCategoryLabel } from '@app/constants/snippetCategoryKeys';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  category: string;
  value: string[];
  onChange: (value: string[]) => void;
}

type PanelMode = 'collapsed' | 'form' | 'checking' | 'result' | 'escalated';

/**
 * Inline panel rendered below the ServiceSelect dropdown. Lets the user
 * propose a service that is not yet in the catalogue, kicks off an
 * LLM-based relevance + categorisation check on the agent service, and
 * — depending on the verdict — either auto-selects the freshly created
 * snippet, explains why no snippet was created, or escalates to a human
 * review ticket on user request.
 *
 * Platform admins (`User.isAdmin`) additionally get an "add anyway" override
 * on every non-positive verdict: the check is re-run with the classifier
 * skipped so the service lands in the chosen category regardless of what the
 * agent said (see `forceServiceCheck` in dieter-server).
 */
export function SuggestServicePanel({ category, value, onChange }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PanelMode>('collapsed');
  const [serviceName, setServiceName] = useState('');
  const [serviceUrl, setServiceUrl] = useState('');
  const [checkId, setCheckId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // True while a forced (admin-override) re-run is in flight, so the
  // "checking" state can explain that no relevance check is happening.
  const [forceRequested, setForceRequested] = useState(false);

  const { user } = useUser();
  const isPlatformAdmin = !!user?.isAdmin;

  const [submit, { loading: submitting }] = useSubmitServiceCheckMutation();
  const [escalate, { loading: escalating }] = useEscalateServiceCheckMutation();
  const [force, { loading: forcing }] = useForceServiceCheckMutation();
  const { data, stopPolling, startPolling } = useServiceCheckQuery(checkId, 3000);
  const { refetch: refetchServices } = useAvailableServicesForCategoryQuery(category);

  const check = data?.serviceCheck;

  // Stop polling and transition to result/escalated as soon as the
  // backend produces a terminal status.
  useEffect(() => {
    if (!check) return;
    if (check.status === 'PENDING') return;
    stopPolling();
    if (check.escalatedAt) {
      setMode('escalated');
      return;
    }
    setMode('result');
    if (check.status === 'COMPLETED' && check.verdict === 'RELEVANT_ADDED') {
      refetchServices().catch(() => undefined);
      if (!value.includes(check.inputServiceName)) {
        onChange([...value, check.inputServiceName]);
      }
    }
    // intentionally not depending on `value` / `onChange` — we only react to
    // status transitions of the check itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check?.status, check?.verdict, check?.escalatedAt]);

  const reset = () => {
    setMode('collapsed');
    setServiceName('');
    setServiceUrl('');
    setCheckId(null);
    setSubmitError(null);
    setForceRequested(false);
    stopPolling();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = serviceName.trim();
    if (!trimmedName) return;
    const rawUrl = serviceUrl.trim();
    // Be lenient: users often type "example.com" without a scheme. Prepend
    // https:// in that case so the value we persist is a usable URL.
    const normalisedUrl = rawUrl && !/^https?:\/\//i.test(rawUrl) ? `https://${rawUrl}` : rawUrl;
    setSubmitError(null);
    try {
      const res = await submit({
        variables: {
          input: {
            serviceName: trimmedName,
            serviceUrl: normalisedUrl || null,
            category,
          },
        },
      });
      const id = res.data?.submitServiceCheck.id;
      if (!id) throw new Error('Server returned no check id');
      setCheckId(id);
      setMode('checking');
    } catch (err: any) {
      setSubmitError(err?.message ?? t('components.block.quest.scan.suggest.unknown_error'));
    }
  };

  const handleForce = async () => {
    if (!checkId) return;
    setSubmitError(null);
    try {
      await force({ variables: { id: checkId } });
      setForceRequested(true);
      setMode('checking');
      // Polling was stopped when the first verdict arrived; the forced
      // re-run flips the row back to PENDING, so resume until it settles.
      startPolling(3000);
    } catch (err: any) {
      setSubmitError(err?.message ?? t('components.block.quest.scan.suggest.unknown_error'));
    }
  };

  const handleEscalate = async () => {
    if (!checkId) return;
    try {
      await escalate({ variables: { id: checkId } });
      setMode('escalated');
    } catch (err: any) {
      setSubmitError(err?.message ?? t('components.block.quest.scan.suggest.unknown_error'));
    }
  };

  if (mode === 'collapsed') {
    return (
      <div className="mt-4 text-sm">
        <button type="button" onClick={() => setMode('form')} className="text-primary underline hover:no-underline">
          {t('components.block.quest.scan.suggest.trigger')}
        </button>
      </div>
    );
  }

  if (mode === 'form') {
    return (
      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 rounded-md border border-gray-200 p-4">
        <div className="text-sm text-gray-700">{t('components.block.quest.scan.suggest.form_intro')}</div>
        <Input
          placeholder={t('components.block.quest.scan.suggest.name_placeholder')}
          value={serviceName}
          onChange={(e) => setServiceName(e.target.value)}
          required
        />
        <Input
          type="text"
          inputMode="url"
          placeholder={t('components.block.quest.scan.suggest.url_placeholder')}
          value={serviceUrl}
          onChange={(e) => setServiceUrl(e.target.value)}
        />
        {submitError && <div className="text-sm text-red-600">{submitError}</div>}
        <div className="flex flex-row gap-2">
          <Button
            type="submit"
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            disabled={!serviceName.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('components.block.quest.scan.suggest.check')}
          </Button>
          <Button type="button" size="small" variant="text" color="inherit" onClick={reset}>
            {t('components.block.quest.scan.suggest.cancel')}
          </Button>
        </div>
      </form>
    );
  }

  if (mode === 'checking') {
    return (
      <div className="mt-4 flex flex-row items-center gap-3 rounded-md border border-gray-200 p-4 text-sm text-gray-700">
        <Spinner color="primary" size="small" />
        <span>
          {forceRequested
            ? t('components.block.quest.scan.suggest.forcing', { service: serviceName })
            : t('components.block.quest.scan.suggest.checking', { service: serviceName })}
        </span>
      </div>
    );
  }

  if (mode === 'result' && check) {
    const isPositive = check.verdict === 'RELEVANT_ADDED';
    const isFailure = check.status === 'FAILED';
    // Escalation only makes sense when our pipeline likely got it wrong, i.e.
    // for NOT_RELEVANT verdicts (or hard FAILUREs). For DUPLICATE the snippet
    // already exists, for WRONG_CATEGORY the user can simply add it to the
    // correct section themselves — no human follow-up needed.
    const canEscalate = check.verdict === 'NOT_RELEVANT' || isFailure;
    // Admin override: platform admins may push the service into the chosen
    // category on any non-positive outcome, including a failed run.
    const canForce = isPlatformAdmin && !isPositive;
    const wasForced = isPositive && !!check.forcedAt;

    return (
      <div
        className={`mt-4 flex flex-col gap-3 rounded-md border p-4 text-sm ${
          isPositive
            ? 'border-green-300 bg-green-50 text-green-900'
            : isFailure
              ? 'border-red-300 bg-red-50 text-red-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'
        }`}
      >
        <div className="font-semibold">
          {isPositive &&
            (wasForced
              ? t('components.block.quest.scan.suggest.forced_added', { service: check.inputServiceName })
              : t('components.block.quest.scan.suggest.added', { service: check.inputServiceName }))}
          {check.verdict === 'NOT_RELEVANT' &&
            t('components.block.quest.scan.suggest.not_relevant', { service: check.inputServiceName })}
          {check.verdict === 'WRONG_CATEGORY' &&
            t('components.block.quest.scan.suggest.wrong_category', { service: check.inputServiceName })}
          {check.verdict === 'DUPLICATE' &&
            t('components.block.quest.scan.suggest.duplicate', { service: check.inputServiceName })}
          {isFailure && t('components.block.quest.scan.suggest.failed')}
        </div>
        {check.reasoning && <div className="whitespace-pre-line">{humanizeCategoryKeysInText(check.reasoning)}</div>}
        {check.verdict === 'WRONG_CATEGORY' && check.suggestedCategory && (
          <div>
            {t('components.block.quest.scan.suggest.recommended_category')}{' '}
            <strong>{snippetCategoryLabel(check.suggestedCategory)}</strong>
          </div>
        )}
        {canForce && <div className="text-xs opacity-80">{t('components.block.quest.scan.suggest.force_hint')}</div>}
        {submitError && <div className="text-red-600">{submitError}</div>}
        <div className="flex flex-row flex-wrap gap-2 pt-1">
          {canForce && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              disabled={forcing || escalating}
              startIcon={forcing ? <CircularProgress size={14} color="inherit" /> : null}
              onClick={handleForce}
              data-testid="suggest-service-force"
            >
              {t('components.block.quest.scan.suggest.force_add')}
            </Button>
          )}
          {canEscalate && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={escalating || forcing}
              startIcon={escalating ? <CircularProgress size={14} color="inherit" /> : null}
              onClick={handleEscalate}
            >
              {t('components.block.quest.scan.suggest.report_anyway')}
            </Button>
          )}
          <Button
            size="small"
            variant={isPositive ? 'contained' : 'text'}
            color={isPositive ? 'primary' : 'inherit'}
            disableElevation
            onClick={reset}
          >
            {isPositive
              ? t('components.block.quest.scan.suggest.continue')
              : t('components.block.quest.scan.suggest.close')}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'escalated') {
    return (
      <div className="mt-4 flex flex-col gap-3 rounded-md border border-green-300 bg-green-50 p-4 text-sm text-green-900">
        <div className="font-semibold">{t('components.block.quest.scan.suggest.escalated_title')}</div>
        <div>{t('components.block.quest.scan.suggest.escalated_body')}</div>
        <div>
          <Button size="small" variant="text" color="inherit" onClick={reset}>
            {t('components.block.quest.scan.suggest.close')}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
